import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { theme } from '@src/common/styles/Theme';
import {
  TractorFloridaVehicleConfigurationField,
  TractorOntarioVehicleTypeField,
} from '@src/permitField/TractorInformationFields';
import type { AddPermitTypeFormInputs } from '@src/permitTypesManager/AddPermitTypeFormInputs';
import { JurisdictionField } from '@src/permitTypesManager/permitTypeSections/PermitDefinitionFields';
import {
  OwnerAddressField,
  OwnerCityField,
  OwnerCompanyNameField,
  OwnerEmailField,
  OwnerPhoneField,
  OwnerStateField,
  OwnerZipCodeField,
  TractorActualWeightField,
  TractorAxleCountField,
  TractorBodyStyleField,
  TractorFuelTypeField,
  TractorGroundHeightField,
  TractorGVWField,
  TractorHeightField,
  TractorIsLeasedField,
  TractorLegalWeightField,
  TractorLengthField,
  TractorLicenseEffectiveDateField,
  TractorLicenseExpirationDateField,
  TractorLicenseIsApportionedPlateField,
  TractorLicensePlateNumberField,
  TractorLicensePlateStateField,
  TractorLicensePlateTypeField,
  TractorLicensePlateYearField,
  TractorMakeField,
  TractorModelNumberField,
  TractorOuterBridgeLengthField,
  TractorOwnerNameField,
  TractorTypeField,
  TractorUnitNumberField,
  TractorUnladenWeightField,
  TractorVINField,
  TractorWheelBaseField,
  TractorWidthField,
  TractorYearField,
  USDOTNumberOfOwningCompanyField,
  VehicleOfSpecialDesignField,
} from '@src/permitTypesManager/permitTypeSections/PermitTractorFields';
import type { SectionField } from '@src/permitTypesManager/permitTypeSections/PermitTypeSectionCheboxList';
import { PermitTypeSectionCheckboxList } from '@src/permitTypesManager/permitTypeSections/PermitTypeSectionCheboxList';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export const PermitTractorSection = () => {
  const { t } = useTranslation();
  const { control } = useFormContext<AddPermitTypeFormInputs>();
  const [permitJurisdiction] = useWatch({
    control,
    name: [JurisdictionField.fieldName],
  });

  const tractorInformationFields: SectionField[] = [
    {
      field: TractorUnitNumberField,
      label: t('fields.tractorUnitNumber'),
    },
    {
      field: TractorMakeField,
      label: t('fields.tractorMake'),
    },
    {
      field: TractorVINField,
      label: t('fields.tractorVIN'),
    },
    {
      field: TractorYearField,
      label: t('fields.tractorYear'),
    },
    {
      field: TractorAxleCountField,
      label: t('fields.tractorAxleCount'),
    },
    {
      field: TractorLicensePlateNumberField,
      label: t('fields.tractorLicensePlateNumber'),
    },
    {
      field: TractorLicensePlateStateField,
      label: t('fields.tractorLicensePlateState'),
    },
    {
      field: TractorModelNumberField,
      label: t('fields.tractorModelNumber'),
    },
    {
      field: TractorTypeField,
      label: t('fields.tractorType'),
    },
    {
      field: TractorFuelTypeField,
      label: t('fields.tractorFuelType'),
    },
    {
      field: TractorBodyStyleField,
      label: t('fields.tractorBodyStyle'),
    },
    {
      field: VehicleOfSpecialDesignField,
      label: t('fields.vehicleOfSpecialDesign'),
    },
  ];

  const licensePlateDetailsFields: SectionField[] = [
    {
      field: TractorLicenseEffectiveDateField,
      label: t('fields.tractorLicenseEffectiveDate'),
    },
    {
      field: TractorLicenseExpirationDateField,
      label: t('fields.tractorLicenseExpirationDate'),
    },
    {
      field: TractorLicensePlateTypeField,
      label: t('fields.tractorLicensePlateType'),
    },
    {
      field: TractorLicensePlateYearField,
      label: t('fields.tractorLicensePlateYear'),
    },
    {
      field: TractorLicenseIsApportionedPlateField,
      label: t('fields.tractorLicenseIsApportionedPlate'),
    },
  ];

  const tractorDimensionsFields: SectionField[] = [
    {
      field: TractorLengthField,
      label: t('fields.tractorLength'),
    },
    {
      field: TractorWidthField,
      label: t('fields.tractorWidth'),
    },
    {
      field: TractorHeightField,
      label: t('fields.tractorHeight'),
    },
    {
      field: TractorWheelBaseField,
      label: t('fields.tractorWheelBase'),
    },
    {
      field: TractorGroundHeightField,
      label: t('fields.tractorGroundHeight'),
    },
    {
      field: TractorUnladenWeightField,
      label: t('fields.tractorUnladenWeight'),
    },
    {
      field: TractorActualWeightField,
      label: t('fields.tractorActualWeight'),
    },
    {
      field: TractorLegalWeightField,
      label: t('fields.tractorLegalWeight'),
    },
    {
      field: TractorOuterBridgeLengthField,
      label: t('fields.tractorOuterBridgeLength'),
    },
    {
      field: TractorGVWField,
      label: t('fields.tractorGVW'),
    },
  ];

  const tractorOwnerFields: SectionField[] = [
    {
      field: TractorIsLeasedField,
      label: t('fields.tractorIsLeased'),
    },
    {
      field: TractorOwnerNameField,
      label: t('fields.tractorOwnerName'),
    },
    {
      field: OwnerCompanyNameField,
      label: t('fields.ownerCompanyName'),
    },
    {
      field: USDOTNumberOfOwningCompanyField,
      label: t('fields.USDOTNumberOfOwningCompany'),
    },
    {
      field: OwnerAddressField,
      label: t('fields.ownerAddress'),
    },
    {
      field: OwnerCityField,
      label: t('fields.ownerCity'),
    },
    {
      field: OwnerStateField,
      label: t('fields.ownerState'),
    },
    {
      field: OwnerZipCodeField,
      label: t('fields.ownerZipCode'),
    },
    {
      field: OwnerPhoneField,
      label: t('fields.ownerPhone'),
    },
    {
      field: OwnerEmailField,
      label: t('fields.ownerEmail'),
    },
  ];

  const flFields: SectionField[] = [
    {
      field: TractorFloridaVehicleConfigurationField,
      label: t('fields.tractorFloridaVehicleConfiguration'),
    },
  ];

  const onFields: SectionField[] = [
    {
      field: TractorOntarioVehicleTypeField,
      label: t('fields.tractorOntarioVehicleType'),
    },
  ];

  return (
    <Stack direction="column" gap={theme.spacing['1']}>
      <Typography style={theme.typography.label.large}>
        {t('tractorInformation')}
      </Typography>
      <PermitTypeSectionCheckboxList
        sectionFields={tractorInformationFields}
        control={control}
      />
      {permitJurisdiction == 'FL' && (
        <PermitTypeSectionCheckboxList
          sectionFields={flFields}
          control={control}
        />
      )}
      {permitJurisdiction == 'ON' && (
        <PermitTypeSectionCheckboxList
          sectionFields={onFields}
          control={control}
        />
      )}
      <Typography style={theme.typography.label.large}>
        {t('licensePlateDetails')}
      </Typography>
      <PermitTypeSectionCheckboxList
        sectionFields={licensePlateDetailsFields}
        control={control}
      />
      <Typography style={theme.typography.label.large}>
        {t('tractorDimensions')}
      </Typography>
      <PermitTypeSectionCheckboxList
        sectionFields={tractorDimensionsFields}
        control={control}
      />
      <Typography style={theme.typography.label.large}>
        {t('tractorOwner')}
      </Typography>
      <PermitTypeSectionCheckboxList
        sectionFields={tractorOwnerFields}
        control={control}
      />
    </Stack>
  );
};
